import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { Button, Tooltip } from "antd";
import { toast } from "react-toastify";

import CheckoutProcessingModal from "../UsageMetrics/components/CheckoutProcessingModal";

import { canMakePayment } from "../../../utils/Permissions";
import { resolveInvoicePaymentPurpose } from "./utils";

import { fetchGyngerCheckoutStatus, openPaymentSession } from "../../../actions/usage";

import { ReactComponent as DueIcon } from "../../../assets/images/subscriptions/due.svg";
import { ReactComponent as ExternallyBilledIcon } from "../../../assets/images/subscriptions/information-line.svg";
import { ReactComponent as PaidIcon } from "../../../assets/images/subscriptions/paid.svg";

import "./InvoicePayment.css";

const CHECKOUT_POLL_INTERVAL_MS = 3000;
const CHECKOUT_POLL_MAX_DURATION_MS = 15 * 60 * 1000;
const CHECKOUT_POLL_MAX_CONSECUTIVE_ERRORS = 5;
const CHECKOUT_FAILED_STATUSES = ["failed", "expired", "canceled", "cancelled", "declined"];
const CHECKOUT_STATUS_UNKNOWN_MESSAGE = "We couldn't confirm your payment status. Please check your billing before trying again.";

const InvoicePaymentContext = createContext(null);

const PAYMENT_STATUSES = {
  due: { label: "Due", icon: DueIcon },
  paid: { label: "Paid", icon: PaidIcon },
  externally_billed: { label: "Externally billed", icon: ExternallyBilledIcon },
};

export function InvoicePaymentProvider({ children, onPaid }) {
  const [payingInvoiceId, setPayingInvoiceId] = useState(null);
  const [paymentPending, setPaymentPending] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  const pollIntervalRef = useRef(null);
  const pollAbortRef = useRef(null);
  const paymentWindowRef = useRef(null);
  const payBusyRef = useRef(false);
  const paymentSettledRef = useRef(false);

  const stopCheckoutPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (pollAbortRef.current) {
      pollAbortRef.current.abort();
      pollAbortRef.current = null;
    }
  }, []);

  const finishPayment = useCallback((outcome) => {
    if (paymentSettledRef.current) return;

    paymentSettledRef.current = true;
    stopCheckoutPolling();
    payBusyRef.current = false;
    paymentWindowRef.current = null;
    setPayingInvoiceId(null);
    setPaymentPending(false);
    setPaymentUrl("");

    if (outcome === "completed") {
      toast.success("Payment successful.");
      onPaid();
      return;
    }

    if (outcome === "unknown") {
      toast.error(CHECKOUT_STATUS_UNKNOWN_MESSAGE);
      return;
    }

    toast.error("Your payment could not be completed. Please try again.");
  }, [onPaid, stopCheckoutPolling]);

  const cancelCheckout = useCallback(() => {
    paymentSettledRef.current = true;
    stopCheckoutPolling();
    if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
      paymentWindowRef.current.close();
    }
    paymentWindowRef.current = null;
    payBusyRef.current = false;
    setPayingInvoiceId(null);
    setPaymentPending(false);
    setPaymentUrl("");
  }, [stopCheckoutPolling]);

  const startCheckoutPolling = useCallback((sessionId) => {
    stopCheckoutPolling();

    const controller = new AbortController();
    pollAbortRef.current = controller;
    setPayingInvoiceId(null);
    setPaymentPending(true);

    const startedAt = Date.now();
    let consecutiveErrors = 0;

    const pollStatus = async () => {
      if (Date.now() - startedAt >= CHECKOUT_POLL_MAX_DURATION_MS) {
        finishPayment("unknown");
        return;
      }

      const response = await fetchGyngerCheckoutStatus(sessionId, controller.signal);
      if (response.canceled || controller.signal.aborted) return;

      if (!response.success) {
        consecutiveErrors += 1;
        const isClientError = response.statusCode >= 400
          && response.statusCode < 500
          && response.statusCode !== 401;
        if (isClientError || consecutiveErrors >= CHECKOUT_POLL_MAX_CONSECUTIVE_ERRORS) {
          finishPayment("unknown");
        }
        return;
      }

      consecutiveErrors = 0;
      const status = String(response.result?.status || "").toLowerCase();
      if (status === "completed") {
        finishPayment("completed");
        return;
      }

      if (CHECKOUT_FAILED_STATUSES.includes(status)) {
        finishPayment("failed");
      }
    };

    pollStatus();
    pollIntervalRef.current = setInterval(pollStatus, CHECKOUT_POLL_INTERVAL_MS);
  }, [finishPayment, stopCheckoutPolling]);

  useEffect(() => () => {
    stopCheckoutPolling();
  }, [stopCheckoutPolling]);

  const payNow = useCallback(async (record) => {
    if (!canMakePayment() || payBusyRef.current) return;

    if (record.invoiceId == null) {
      toast.error("Unable to start payment for this invoice.");
      return;
    }

    const paymentWindow = window.open("about:blank", "_blank");
    paymentWindowRef.current = paymentWindow;
    if (!paymentWindow) {
      toast.error("Pop-up blocked. Allow pop-ups to complete payment.");
      return;
    }

    paymentSettledRef.current = false;
    payBusyRef.current = true;
    setPayingInvoiceId(record.invoiceId);

    const checkoutResponse = await openPaymentSession({
      purpose: resolveInvoicePaymentPurpose(record.type),
      amount: record.amount,
      productDescription: record.name,
      invoiceId: record.invoiceId,
    });

    if (checkoutResponse.canceled) {
      paymentWindow.close();
      payBusyRef.current = false;
      setPayingInvoiceId(null);
      return;
    }

    if (!checkoutResponse.success) {
      paymentWindow.close();
      payBusyRef.current = false;
      setPayingInvoiceId(null);
      toast.error(checkoutResponse.message || "Unable to start payment for this invoice.");
      return;
    }

    const sessionId = checkoutResponse.session_id;
    if (!sessionId) {
      paymentWindow.close();
      payBusyRef.current = false;
      setPayingInvoiceId(null);
      toast.error("Unable to open payment session.");
      return;
    }

    setPaymentUrl(checkoutResponse.session_url || "");
    paymentWindow.location.href = checkoutResponse.session_url;

    const status = String(checkoutResponse.status || "").toLowerCase();
    if (status === "completed") {
      paymentWindow.close();
      finishPayment("completed");
      return;
    }

    if (CHECKOUT_FAILED_STATUSES.includes(status)) {
      paymentWindow.close();
      finishPayment("failed");
      return;
    }

    startCheckoutPolling(sessionId);
  }, [finishPayment, startCheckoutPolling]);

  return (
    <InvoicePaymentContext.Provider
      value={{
        payingInvoiceId,
        paymentPending,
        payNow,
      }}
    >
      {children}
      <CheckoutProcessingModal
        open={paymentPending}
        paymentUrl={paymentUrl}
        onCancelPayment={cancelCheckout}
      />
    </InvoicePaymentContext.Provider>
  );
}

export default function InvoicePayment({ record, status }) {
  const payment = useContext(InvoicePaymentContext);
  const statusKey = String(status ?? "").trim().toLowerCase().replace(/\s+/g, "_");
  if (!PAYMENT_STATUSES[statusKey]) return null;

  const resolvedKey = statusKey;
  const statusConfig = PAYMENT_STATUSES[resolvedKey];
  const StatusIcon = statusConfig.icon;
  const isDue = resolvedKey === "due";
  const statusLabel = (
    <span className={`InvoicePayment__Status InvoicePayment__Status--${resolvedKey}`}>
      <StatusIcon className="InvoicePayment__StatusIcon" aria-hidden />
      {statusConfig.label}
    </span>
  );

  if (resolvedKey === "externally_billed") {
    return (
      <Tooltip
        title="Payment is managed outside Genista."
        placement="top"
        overlayClassName="InvoicePayment__ExternalTooltip"
      >
        {statusLabel}
      </Tooltip>
    );
  }

  if (!isDue) {
    return statusLabel;
  }

  const isPaying = payment.payingInvoiceId === record.invoiceId;

  return (
    <div className="InvoicePayment__Cell">
      {statusLabel}
      <Button
        type="primary"
        size="small"
        loading={isPaying}
        disabled={isPaying || payment.paymentPending || !canMakePayment()}
        onClick={() => payment.payNow(record)}
      >
        Pay Now
      </Button>
    </div>
  );
}
